// ═══════════════════════════════════════════════════════════════════════════
// LowCreditBanner — soft, fixed-position chip that appears when a user is
// running low on credits but not yet at zero. Pure transparency-UX:
// never surprises the user. Dismissable for the session.
//
// Thresholds:
//   • Shows when total credits ≤ 50 and > 0
//   • Hides entirely at zero (at that point InsufficientCreditsModal handles it)
//
// Dismiss state is per-session (sessionStorage) so it reappears on next login.
// Listens for `credits:changed` so dismissing + spending doesn't leave a
// stale banner on screen.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { getUserCredits } from '../../lib/credits';

const LOW_THRESHOLD    = 50;
const DISMISS_KEY      = 'eai_low_credit_banner_dismissed';

export default function LowCreditBanner({ onBuyTopup, onUpgrade }) {
  const { user, profile } = useAuth();
  const [credits, setCredits] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; }
    catch { return false; }
  });

  const load = useCallback(async () => {
    if (!user?.id) { setCredits(null); return; }
    try { setCredits(await getUserCredits(user.id)); }
    catch { /* silently ignore */ }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    function h() { load(); }
    window.addEventListener('credits:changed', h);
    return () => window.removeEventListener('credits:changed', h);
  }, [load]);

  function handleDismiss() {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch {}
    setDismissed(true);
  }

  if (!user || !credits) return null;

  const total = (credits.balance ?? 0) + (credits.topup_balance ?? 0);
  if (total <= 0 || total > LOW_THRESHOLD || dismissed) return null;

  const isFree = (profile?.plan || 'free') === 'free';
  const refillStr = refillLabel(credits);

  return (
    <div style={styles.banner} role="status" aria-live="polite">
      <div style={styles.icon}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <div style={styles.copy}>
        <strong>{total}</strong> {total === 1 ? 'credit' : 'credits'} remaining.
        {refillStr && <> Refills {refillStr}, or </>}
        {!refillStr && <> </>}
        top up anytime.
      </div>
      <div style={styles.actions}>
        <button onClick={onBuyTopup} style={styles.topupBtn} type="button">
          Top up
        </button>
        {isFree && (
          <button onClick={onUpgrade} style={styles.upgradeBtn} type="button">
            Upgrade
          </button>
        )}
        <button onClick={handleDismiss} style={styles.closeBtn} aria-label="Dismiss" type="button">×</button>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function refillLabel(credits) {
  if (!credits?.last_refill_at) return null;
  const nextMs = new Date(credits.last_refill_at).getTime() + 30 * 24 * 60 * 60 * 1000;
  const diff = nextMs - Date.now();
  if (diff <= 0) return 'soon';
  const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
  if (days === 1) return 'tomorrow';
  if (days <= 7) return `in ${days} days`;
  return `on ${new Date(nextMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

// ─── Styles ──────────────────────────────────────────────────────────────

const styles = {
  banner: {
    position: 'fixed',
    bottom: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 90,
    maxWidth: 'calc(100vw - 40px)',
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '10px 14px 10px 16px',
    borderRadius: 999,
    background: 'rgba(20, 16, 16, 0.96)',
    border: '1px solid rgba(255,177,1,0.28)',
    color: 'var(--text, #f9f0e6)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,177,1,0.06)',
    fontSize: 13,
    backdropFilter: 'blur(12px)',
  },
  icon: {
    flexShrink: 0,
    color: 'var(--gold, #ffb101)',
    display: 'inline-flex', alignItems: 'center',
  },
  copy: {
    color: 'rgba(249,240,230,0.85)',
    lineHeight: 1.4,
    flex: 1, minWidth: 0,
  },
  actions: {
    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
  },
  topupBtn: {
    padding: '6px 12px', borderRadius: 999,
    background: 'var(--gold, #ffb101)',
    color: '#1a0800',
    border: '1px solid transparent',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  upgradeBtn: {
    padding: '6px 12px', borderRadius: 999,
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'var(--text, #f9f0e6)',
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  closeBtn: {
    width: 24, height: 24, borderRadius: 999,
    border: 'none',
    background: 'rgba(255,255,255,0.04)',
    color: 'rgba(249,240,230,0.55)',
    fontSize: 16, lineHeight: 1, cursor: 'pointer',
    marginLeft: 4,
  },
};
